// ============================================================================
// SHOW ACCESS SCOPE
// ----------------------------------------------------------------------------
// Single source of truth for "which shows can this user see?".
// Shared by showService (show lists) and episodeService (episode lists) so the
// show filter dropdown and the episodes it filters can never drift apart.
// RLS still enforces access server-side; this only shapes the queries.
// ============================================================================

import { supabase } from "@/integrations/supabase/client";

/**
 * Show IDs delegated to the user: accepted collaborations plus
 * network-admin assignments. Excludes shows the user owns.
 */
export async function getDelegatedShowIds(userId: string): Promise<string[]> {
  const [{ data: collabRows }, { data: naRows }] = await Promise.all([
    supabase
      .from("show_collaborators")
      .select("show_id")
      .eq("user_id", userId)
      .not("accepted_at", "is", null),
    supabase.from("network_admin_shows").select("show_id").eq("user_id", userId),
  ]);

  return Array.from(
    new Set([
      ...(collabRows ?? []).map((r) => r.show_id),
      ...(naRows ?? []).map((r) => r.show_id),
    ]),
  );
}

/**
 * All show IDs the user may read.
 * Returns "all" for platform admins — callers should skip the `.in()` filter.
 */
export async function getAccessibleShowIds(
  userId: string,
  isAdmin = false,
): Promise<string[] | "all"> {
  if (isAdmin) return "all";

  const [{ data: ownedShows }, delegated] = await Promise.all([
    supabase.from("shows").select("id").eq("user_id", userId),
    getDelegatedShowIds(userId),
  ]);

  return Array.from(
    new Set([...(ownedShows ?? []).map((s) => s.id), ...delegated]),
  );
}
